import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {  SystemModule, ModulePermissions, UserPermissions } from '../types/permissions';

export interface PermissionCheck {
  hasAccess: (module: SystemModule) => boolean;
  canRead: (module: SystemModule) => boolean;
  canCreate: (module: SystemModule) => boolean;
  canUpdate: (module: SystemModule) => boolean;
  canDelete: (module: SystemModule) => boolean;
  hasPermission: (module: SystemModule, action: 'read' | 'create' | 'update' | 'delete') => boolean;
  isAdmin: boolean;
  permissions: UserPermissions | null;
}

/**
 * Hook para verificar permiss�es do usu�rio
 */
export const usePermissions = (): PermissionCheck => {
  const { user } = useAuth();

  const permissions = useMemo(() => user?.permissoes || null, [user]);
  const isAdmin = useMemo(() => user?.tipo === 'admin', [user]);

  const getModulePermissions = (module: SystemModule): ModulePermissions | null => {
    if (!permissions) return null;

    const permKey = `perm_${module}` as keyof UserPermissions;
    const modulePerms = permissions[permKey];

    if (typeof modulePerms === 'object' && modulePerms !== null && 'read' in modulePerms) {
      return modulePerms as ModulePermissions;
    }

    return null;
  };

  const hasAccess = (module: SystemModule): boolean => {
    if (isAdmin) return true;
    if (!permissions) return false;

    const accessKey = `acesso_${module}` as keyof UserPermissions;
    return permissions[accessKey] === true;
  };

  const canRead = (module: SystemModule): boolean => {
    if (isAdmin) return true;
    if (!hasAccess(module)) return false;

    const modulePerms = getModulePermissions(module);
    return modulePerms?.read === true;
  };

  const canCreate = (module: SystemModule): boolean => {
    if (isAdmin) return true;
    if (!hasAccess(module)) return false;

    const modulePerms = getModulePermissions(module);
    return modulePerms?.create === true;
  };

  const canUpdate = (module: SystemModule): boolean => {
    if (isAdmin) return true;
    if (!hasAccess(module)) return false;

    const modulePerms = getModulePermissions(module);
    return modulePerms?.update === true;
  };

  const canDelete = (module: SystemModule): boolean => {
    if (isAdmin) return true;
    if (!hasAccess(module)) return false;

    const modulePerms = getModulePermissions(module);
    return modulePerms?.delete === true;
  };

  const hasPermission = (
    module: SystemModule,
    action: 'read' | 'create' | 'update' | 'delete'
  ): boolean => {
    switch (action) {
      case 'read':
        return canRead(module);
      case 'create':
        return canCreate(module);
      case 'update':
        return canUpdate(module);
      case 'delete':
        return canDelete(module);
      default:
        return false;
    }
  };

  return {
    hasAccess,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasPermission,
    isAdmin,
    permissions
  };
};
